import React, { createContext, useCallback, useContext, useMemo, useRef } from 'react';
import { DocumentService, sharedApiClient } from '../fetcher';
import useDocumentUploads from './useDocumentUploads';

const DocumentUploadsContext = createContext(null);

/**
 * Keeps the upload queue mounted above the routes, so leaving the documents list
 * (for example to split a PDF) does not drop in-progress uploads or the
 * "delete the original once every slice succeeds" wait.
 */
export const DocumentUploadsProvider = ({ children }) => {
  const documentService = useMemo(() => new DocumentService(sharedApiClient), []);
  const refreshRef = useRef(null);
  const refreshDocuments = useCallback(() => {
    refreshRef.current?.();
  }, []);
  const uploads = useDocumentUploads({ documentService, refreshDocuments });

  const registerRefreshDocuments = useCallback((refresh) => {
    refreshRef.current = refresh;
    return () => {
      if (refreshRef.current === refresh) refreshRef.current = null;
    };
  }, []);

  const value = {
    ...uploads,
    registerRefreshDocuments,
  };

  return (
    <DocumentUploadsContext.Provider value={value}>
      {children}
    </DocumentUploadsContext.Provider>
  );
};

export const useDocumentUploadsContext = () => {
  const context = useContext(DocumentUploadsContext);
  if (!context) {
    throw new Error('useDocumentUploadsContext must be used within DocumentUploadsProvider');
  }
  return context;
};
